import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogCategory } from "@/app/types/BlogCategory";
import { BlogSlugs, PublicPaths } from "@/app/vars/Routes";

export const callToolsArticle: BlogArticle = {
  key: BlogArticleKey.CALL_ANALYSIS_TOOLS,
  slug: BlogSlugs.CALL_ANALYSIS_TOOLS,
  categoryId: BlogCategory.AI_TOOLS,
  date: "2026-07-18",
  updated: "2026-09-29",
  readTime: 8,
  locales: {
    fr: {
      title: "Outil d'analyse d'appels de vente : Claap, Leexi, Modjo, tl;dv",
      excerpt:
        "Le marché a explosé, et on s'y perd. Un tour d'horizon honnête des outils, leurs forces, leurs limites, et la vraie question à se poser avant d'en choisir un.",
      description:
        "Vous utilisez sûrement déjà un outil pour analyser vos appels. Est-ce le bon pour votre besoin, et exploitez-vous vraiment ce qu'il en sort ?",
      ctaTitle: "Voyez ce que ça donne sur vos vrais appels",
      ctaText:
        "Importez un de vos échanges dans une fiche client et voyez ce qu'il en reste : une synthèse, les engagements pris, les passages clés, retrouvables quand vous en avez besoin. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "En 2026, analyser ses appels de vente n'a jamais été aussi simple. Une dizaine d'outils enregistrent, transcrivent et résument vos conversations en quelques minutes. Le problème n'est plus *de pouvoir le faire*, mais de **choisir l'outil adapté à votre besoin**, et de savoir quoi faire des données une fois qu'elles sont là.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Il n'existe pas de meilleur outil dans l'absolu. Il y a un meilleur outil pour un objectif donné. Voici comment situer les principales options, honnêtement, pour vous aider à choisir.",
        },
        { type: ArticleBlockType.H2, text: "Les trois familles d'outils d'analyse d'appels" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Avant de comparer, il faut comprendre que ces outils ne jouent pas dans la même catégorie :",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Les notetakers** : ils transcrivent et résument. Simples, rapides, parfaits pour ne rien oublier d'une réunion. Exemples : tl;dv, Fathom, Fireflies.",
            "**Les plateformes de conversation intelligence** : elles vont plus loin : détection d'objections, coaching, analyse de performance. Exemples : Modjo, Leexi, Claap.",
            "**Les plateformes de revenue intelligence** : lourdes, pensées pour les grandes équipes commerciales, avec prévisions et pilotage du pipeline. Exemples : Gong, Clari.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Selon que vous êtes coach indépendant, fondateur, ou manager d'une équipe de dix commerciaux, votre besoin n'est pas le même. Regardons les options concrètes.",
        },
        { type: ArticleBlockType.H2, text: "Le tour des outils, forces et limites" },
        {
          type: ArticleBlockType.TOOL,
          name: "tl;dv",
          tag: "Le plus simple",
          desc: "Un notetaker efficace : transcription, résumés, partage d'extraits vidéo. Très bon pour la collaboration asynchrone et garder une trace propre de chaque appel.",
          limit:
            "il capture, il ne creuse pas. Peu d'analyse commerciale de fond (patterns d'objections sur plusieurs appels, signaux d'achat).",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Fathom",
          tag: "Léger et gratuit",
          desc: "Résumés de réunions et points d'action, intégration rapide à Zoom et Google Meet, configuration minimale. Idéal pour les petites équipes qui veulent l'essentiel sans complexité.",
          limit:
            "fonctionnalités volontairement limitées. Documentation de réunion plus qu'analyse de vente.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Leexi",
          tag: "Simple et conforme",
          desc: "Transcription et suivi de réunions, avec une bonne intégration aux systèmes téléphoniques (VoIP) et un accent sur la sécurité et la conformité. Pratique pour les équipes qui veulent une solution sans fioritures.",
          limit:
            "reste centré sur la transcription et le suivi. L'exploitation avancée des données est limitée.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Modjo",
          tag: "Le coaching d'équipe",
          desc: "Plateforme française, conforme RGPD, avec de solides flux de coaching : tableaux de bord, suivi des objections, analyse de performance, intégration CRM. Pensée pour les équipes commerciales qui veulent progresser.",
          limit:
            "orientée équipe et manager. Moins adaptée au coach indépendant ou au fondateur solo qui gère plusieurs contextes clients différents.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Claap",
          tag: "La vidéo asynchrone",
          desc: "Plutôt un wiki vidéo qu'un moteur d'analyse : bibliothèque d'appels, partage d'extraits, indexation par mots-clés. Excellent pour réduire les réunions et diffuser des démos.",
          limit:
            "l'analyse commerciale de fond n'est pas son cœur. Fort sur le partage, plus léger sur les insights de vente.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Gong / Clari",
          tag: "Les grands comptes",
          desc: "Les poids lourds de la revenue intelligence : analyse à grande échelle, prévisions, pilotage du pipeline. Puissants pour une force de vente structurée.",
          limit:
            "coûteux, longs à déployer (souvent plusieurs semaines de configuration), surdimensionnés si vous n'avez pas une grande équipe.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "ChatGPT / Claude",
          tag: "L'option DIY",
          desc: "Vous pouvez tout à fait coller un transcript dans ChatGPT ou Claude et demander un résumé, les objections, les douleurs. Rapide, flexible, et souvent étonnamment pertinent sur un appel isolé.",
          limit:
            "l'IA générique part de zéro à chaque fois. Elle ne connaît pas votre client, ne garde pas le contexte d'un appel à l'autre, et vous oblige à réexpliquer qui est le prospect à chaque prompt. Vous refaites le travail sans arrêt.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Votre besoin", "L'option adaptée"],
          rows: [
            ["Ne rien oublier d'une réunion", "tl;dv, Fathom"],
            ["Coacher une équipe commerciale", "Modjo"],
            ["Piloter un gros pipeline", "Gong, Clari"],
            ["Partager des extraits vidéo", "Claap"],
            ["Analyser un appel vite fait", "ChatGPT, Claude"],
          ],
        },
        { type: ArticleBlockType.H2, text: "Le vrai angle mort de tous ces outils" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'Vous avez peut-être remarqué le point commun. Tous ces outils **analysent bien**. Certains mieux que d\'autres, mais globalement, la brique "analyse" est devenue un standard.',
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Le problème n'a jamais été d'analyser un appel. C'est ce qui se passe après.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une fois l'analyse faite, où va la donnée ? Dans un transcript. Un tableau de bord. Un résumé que vous lisez une fois. Un chat Claude que vous fermez. **Elle meurt, isolée, à l'endroit où elle a été produite.**",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Pourtant, cette donnée vaut de l'or quand elle s'accumule. L'objection entendue lundi devrait rejoindre la fiche du client, à côté de ce qu'il a promis et de l'état de son deal. Les objections de cinquante appels devraient pouvoir se compter. Et le commercial qui reprend un compte devrait retrouver ce que le client a dit, mot pour mot, sans réécouter trois heures d'enregistrement.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Au lieu de ça, chacun garde ce qu'il a entendu dans ses notes ou dans sa tête, client par client. C'est là que se perd le vrai temps, et la vraie valeur.",
        },
        { type: ArticleBlockType.H2, text: "La bonne question à se poser" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'Quand on choisit un outil, on demande : "lequel analyse le mieux mes appels ?" C\'est la mauvaise question, ou du moins une question incomplète.',
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La bonne question est : **\"lequel rend cette donnée réutilisable, client par client et sur l'ensemble de mes échanges ?\"** Parce que l'analyse n'est qu'un point de départ. Ce qui change votre quotidien, c'est qu'une donnée captée une fois ressorte au bon moment, sans que vous la ressaisissiez.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "C'est exactement là que se place Meidly",
          text: "Chaque échange, capté sur Google Meet par l'extension Chrome, importé en fichier audio ou vidéo ou via un lien Google Drive, rejoint la fiche de son client. On le transcrit, on en tague les passages clés (objections, signaux d'achat, risques de churn), on met à jour la synthèse du client, ses engagements avec leur échéance et ses deals. Et l'assistant répond sur l'ensemble des échanges en citant chaque passage.",
          linkLabel: "Voir les fiches clients",
          linkPath: PublicPaths.HOME,
        },
        { type: ArticleBlockType.H2, text: "Alors, quel outil choisir ?" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Si votre seul besoin est de garder une trace de vos réunions, un notetaker comme tl;dv ou Fathom suffit. Si vous coachez une équipe, Modjo est solide. Si vous pilotez un gros pipeline, Gong fait le travail.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Mais si votre problème n'est pas *d'analyser*, mais de **transformer ce que disent vos clients en décisions concrètes**, comprendre qui achète et pourquoi, suivre ce qui a été promis, savoir ce qui revient d'un client à l'autre, alors un outil d'analyse seul ne suffira pas. Il vous faut un endroit où cette donnée s'accumule, client par client.",
        },
      ],
      faq: [
        {
          question: "Quel est le meilleur outil pour analyser ses appels de vente ?",
          answer:
            "Il n'y a pas de meilleur outil universel. Pour ne rien oublier d'une réunion, un notetaker comme tl;dv ou Fathom suffit. Pour coacher une équipe, Modjo est solide. Pour un gros pipeline, Gong. Le vrai critère n'est pas seulement la qualité de l'analyse, mais ce que vous faites de la donnée ensuite.",
        },
        {
          question: "Peut-on analyser ses appels de vente avec ChatGPT ou Claude ?",
          answer:
            "Oui, coller un transcript dans ChatGPT ou Claude donne souvent de bons résultats sur un appel isolé. La limite, c'est que l'IA générique repart de zéro à chaque fois : elle ne connaît pas votre client, ne garde pas le contexte d'un appel à l'autre, et vous oblige à tout réexpliquer à chaque prompt.",
        },
        {
          question:
            "Quelle est la différence entre un notetaker et une plateforme de conversation intelligence ?",
          answer:
            "Un notetaker (tl;dv, Fathom) transcrit et résume. Une plateforme de conversation intelligence (Modjo, Leexi, Claap) va plus loin : détection d'objections, coaching, analyse de performance sur l'ensemble des appels.",
        },
        {
          question: "Analyser ses appels suffit-il à mieux vendre ?",
          answer:
            "Non. L'analyse est un point de départ. La vraie valeur vient de ce que devient la donnée ensuite : rattachée à la fiche de chaque client, avec ses engagements et ses deals, et interrogeable sur l'ensemble des échanges, chaque réponse citant le passage exact. C'est là que la plupart des outils s'arrêtent.",
        },
      ],
    },
    en: {
      title: "Which tool to analyze your sales calls? (Leexi, Modjo, tl;dv)",
      excerpt:
        "The market has exploded, and it's easy to get lost. An honest overview of the tools, their strengths, their limits, and the real question to ask before choosing one.",
      description:
        "You are probably already using a tool to analyse your calls. Is it the right one for your need, and do you use what it produces?",
      ctaTitle: "See what it looks like on your real calls",
      ctaText:
        "Import one of your conversations into a customer file and see what is left of it: a synthesis, the commitments made, the key passages, easy to find when you need them. No commitment.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "In 2026, analyzing your sales calls has never been easier. A dozen tools record, transcribe and summarize your conversations in minutes. The problem is no longer *being able to do it*, but **choosing the tool that fits your need**, and knowing what to do with the data once it's there.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "There is no best tool in absolute terms. There is a best tool for a given goal. Here is how to situate the main options, honestly, to help you choose.",
        },
        { type: ArticleBlockType.H2, text: "The three families of call analysis tools" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Before comparing, you need to understand that these tools don't play in the same category:",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Notetakers**: they transcribe and summarize. Simple, fast, perfect for not forgetting anything from a meeting. Examples: tl;dv, Fathom, Fireflies.",
            "**Conversation intelligence platforms**: they go further: objection detection, coaching, performance analysis. Examples: Modjo, Leexi, Claap.",
            "**Revenue intelligence platforms**: heavy, built for large sales teams, with forecasting and pipeline management. Examples: Gong, Clari.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Whether you're an independent coach, a founder, or a manager of a team of ten reps, your need isn't the same. Let's look at the concrete options.",
        },
        { type: ArticleBlockType.H2, text: "A tour of the tools, strengths and limits" },
        {
          type: ArticleBlockType.TOOL,
          name: "tl;dv",
          tag: "The simplest",
          desc: "An effective notetaker: transcription, summaries, video clip sharing. Very good for asynchronous collaboration and keeping a clean record of every call.",
          limit:
            "it captures, it doesn't dig. Little deep sales analysis (objection patterns across calls, buying signals).",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Fathom",
          tag: "Light and free",
          desc: "Meeting summaries and action items, quick integration with Zoom and Google Meet, minimal setup. Ideal for small teams who want the essentials without complexity.",
          limit: "deliberately limited features. Meeting documentation more than sales analysis.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Leexi",
          tag: "Simple and compliant",
          desc: "Transcription and meeting tracking, with good integration to phone systems (VoIP) and a focus on security and compliance. Handy for teams who want a no-frills solution.",
          limit:
            "stays centered on transcription and tracking. Advanced use of the data is limited.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Modjo",
          tag: "Team coaching",
          desc: "A French platform, GDPR compliant, with solid coaching flows: dashboards, objection tracking, performance analysis, CRM integration. Built for sales teams that want to improve.",
          limit:
            "team and manager oriented. Less suited to the solo coach or founder juggling several different client contexts.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Claap",
          tag: "Async video",
          desc: "More of a video wiki than an analysis engine: call library, clip sharing, keyword indexing. Excellent for cutting down meetings and sharing demos.",
          limit:
            "deep sales analysis isn't its core. Strong on sharing, lighter on sales insights.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Gong / Clari",
          tag: "Enterprise",
          desc: "The heavyweights of revenue intelligence: large-scale analysis, forecasting, pipeline management. Powerful for a structured sales force.",
          limit:
            "expensive, slow to deploy (often several weeks of setup), oversized if you don't have a large team.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "ChatGPT / Claude",
          tag: "The DIY option",
          desc: "You can absolutely paste a transcript into ChatGPT or Claude and ask for a summary, the objections, the pains. Fast, flexible, and often surprisingly relevant on a single call.",
          limit:
            "generic AI starts from scratch every time. It doesn't know your customer, doesn't keep context from one call to the next, and forces you to re-explain who the prospect is on every prompt. You redo the work over and over.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Your need", "The right option"],
          rows: [
            ["Not forgetting anything from a meeting", "tl;dv, Fathom"],
            ["Coaching a sales team", "Modjo"],
            ["Managing a large pipeline", "Gong, Clari"],
            ["Sharing video clips", "Claap"],
            ["Analyzing a call quickly", "ChatGPT, Claude"],
          ],
        },
        { type: ArticleBlockType.H2, text: "The real blind spot of all these tools" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'You may have noticed the common thread. All these tools **analyze well**. Some better than others, but overall, the "analysis" building block has become a standard.',
        },
        {
          type: ArticleBlockType.LEAD,
          text: "The problem was never analyzing a call. It's what happens afterwards.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Once the analysis is done, where does the data go? Into a transcript. A dashboard. A summary you read once. A Claude chat you close. **It dies, isolated, where it was produced.**",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Yet this data is worth gold once it accumulates. The objection heard on Monday should land in the customer's file, next to what they promised and where their deal stands. The objections of fifty calls should be countable. And the rep taking over an account should find what the customer said, word for word, without replaying three hours of recordings.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Instead, everyone keeps what they heard in their notes or in their head, customer by customer. That's where the real time, and the real value, is lost.",
        },
        { type: ArticleBlockType.H2, text: "The right question to ask" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'When choosing a tool, people ask: "which one analyzes my calls best?" That\'s the wrong question, or at least an incomplete one.',
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'The right question is: **"which one makes that data reusable, customer by customer and across all my conversations?"** Because analysis is only a starting point. What changes your day-to-day is data captured once resurfacing at the right moment, without you re-entering it.',
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "This is exactly where Meidly sits",
          text: "Every conversation, captured on Google Meet by the Chrome extension, uploaded as an audio or video file or through a Google Drive link, lands in its customer's file. We transcribe it, tag its key passages (objections, buying signals, churn risks), and update the customer's synthesis, their commitments with due dates and their deals. And the assistant answers across every conversation, citing each passage.",
          linkLabel: "See the customer files",
          linkPath: PublicPaths.HOME,
        },
        { type: ArticleBlockType.H2, text: "So, which tool should you choose?" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "If your only need is to keep a record of your meetings, a notetaker like tl;dv or Fathom is enough. If you coach a team, Modjo is solid. If you manage a large pipeline, Gong does the job.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "But if your problem isn't *analyzing*, but **turning what your customers say into concrete decisions**, understanding who buys and why, tracking what was promised, knowing what comes back from one customer to the next, then an analysis tool alone won't be enough. You need a place where that data accumulates, customer by customer.",
        },
      ],
      faq: [
        {
          question: "What is the best tool to analyze sales calls?",
          answer:
            "There is no universal best tool. To not forget anything from a meeting, a notetaker like tl;dv or Fathom is enough. To coach a team, Modjo is solid. For a large pipeline, Gong. The real criterion isn't just analysis quality, but what you do with the data next.",
        },
        {
          question: "Can you analyze sales calls with ChatGPT or Claude?",
          answer:
            "Yes, pasting a transcript into ChatGPT or Claude often gives good results on a single call. The limit is that generic AI starts from scratch every time: it doesn't know your customer, doesn't keep context from one call to the next, and forces you to re-explain everything on every prompt.",
        },
        {
          question:
            "What's the difference between a notetaker and a conversation intelligence platform?",
          answer:
            "A notetaker (tl;dv, Fathom) transcribes and summarizes. A conversation intelligence platform (Modjo, Leexi, Claap) goes further: objection detection, coaching, performance analysis across all calls.",
        },
        {
          question: "Is analyzing your calls enough to sell better?",
          answer:
            "No. Analysis is a starting point. The real value comes from what happens to the data next: attached to each customer's file, with their commitments and deals, and searchable across every conversation, each answer citing the exact passage. That's where most tools stop.",
        },
      ],
    },
  },
};
